// Utility helpers for cleaning backend 'dart' segmentation paths.
// The backend stores paths in a custom "M,y,xQ,y,x,y,xQ..." format (y,x pairs).
// We parse them, split by large jumps, pick the main curve and return cleaned points.
export type Point = { x: number; y: number };

export function parseDartPoints(raw: string): Point[] {
  const coords: Point[] = [];

  if (!raw || typeof raw !== 'string') return coords;

  // M command (backend stores as M,y,x)
  const mMatch = raw.match(/M,?([0-9.-]+),?([0-9.-]+)/);
  if (mMatch) {
    const y = parseFloat(mMatch[1]);
    const x = parseFloat(mMatch[2]);
    if (!Number.isNaN(x) && !Number.isNaN(y)) coords.push({ x, y });
  }

  // Q segments (each contains alternating y,x values)
  const qParts = raw.split(/Q/);
  for (let i = 1; i < qParts.length; i++) {
    const segment = qParts[i];
    const numbers = segment.match(/[-0-9.]+/g);
    if (!numbers) continue;
    for (let j = 0; j < numbers.length - 1; j += 2) {
      const y = parseFloat(numbers[j]);
      const x = parseFloat(numbers[j + 1]);
      if (!Number.isNaN(x) && !Number.isNaN(y)) coords.push({ x, y });
    }
  }

  return coords;
}

export function splitByDistance(points: Point[], maxJump = 80): Point[][] {
  const groups: Point[][] = [];
  if (!points || points.length === 0) return groups;
  let current: Point[] = [points[0]];
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    const d = Math.hypot(b.x - a.x, b.y - a.y);
    if (d > maxJump) {
      groups.push(current);
      current = [b];
    } else {
      current.push(b);
    }
  }
  if (current.length > 0) groups.push(current);
  return groups;
}

export function pickMainCurve(groups: Point[][]): Point[] {
  if (!groups || groups.length === 0) return [];
  // Choose the longest group (most points)
  let best = groups[0];
  for (let i = 1; i < groups.length; i++) {
    if (groups[i].length > best.length) best = groups[i];
  }
  return best;
}

export function cleanDartPath(raw: string, maxJump = 80): Point[] {
  const pts = parseDartPoints(raw);
  const groups = splitByDistance(pts, maxJump);
  const main = pickMainCurve(groups);

  // Optional: remove tiny tails (sometimes last 1-2 points are outliers)
  // Keep only if it still looks reasonable.
  if (main.length >= 5) {
    // drop any final point that makes a huge jump from previous (extra safety)
    while (main.length >= 2) {
      const a = main[main.length - 2];
      const b = main[main.length - 1];
      const d = Math.hypot(b.x - a.x, b.y - a.y);
      if (d > maxJump) main.pop();
      else break;
    }
  }

  return main;
}

export function pointsToSvgPath(points: Point[]): string {
  if (!points || points.length === 0) return '';
  const [first, ...rest] = points;
  let path = `M ${first.x} ${first.y}`;
  for (const p of rest) {
    path += ` L ${p.x} ${p.y}`;
  }
  path += ' Z';
  return path;
}
